import { type JSX } from 'react'
import { useNavigate } from 'react-router'
import { AlertTriangle, CheckCircle2, XCircle, GitBranch } from 'lucide-react'
import type { ConversationListEntry, SlaState } from './types.js'
import { getChannelConfig } from './channelConfig.js'
import { cn } from '../../utils/cn.js'
import { formatDateTime, timestampMs } from '../../utils/timezone.js'
import { formatCustomerName, getCustomerInitials } from '../../utils/formatCustomerName.js'

export interface ConversationListItemProps {
  entry: ConversationListEntry
  selected: boolean
  onClick: () => void
  isFirst?: boolean
  isLast?: boolean
  now: number
  ratingScale: number
}

const SLA_COLORS: Record<SlaState, string> = {
  within: 'bg-emerald-500',
  approaching: 'bg-amber-500',
  breached: 'bg-red-500',
  none: 'bg-gray-200',
}

function relativeTime(iso: string, now: number = Date.now()): string {
  const t = timestampMs(iso)
  const diff = Math.max(0, now - t)
  const s = Math.floor(diff / 1000)
  if (s < 60) return 'now'
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  const formatted = formatDateTime(iso)
  return formatted.split(',')[0] ?? formatted
}

function humanizeLive(ms: number): string {
  const abs = Math.abs(ms)
  const totalSecs = Math.floor(abs / 1000)
  const days = Math.floor(totalSecs / 86400)
  const hours = Math.floor(totalSecs / 3600)
  const mins = Math.floor((totalSecs % 3600) / 60)
  const secs = totalSecs % 60
  if (days >= 1) return `${days}d ${hours}h`
  if (hours >= 1) return `${hours}h ${mins}m`
  if (mins >= 1) return `${mins}m ${secs}s`
  return `${secs}s`
}

interface MiniTimer {
  label: string
  dueAt: string | null
  completedAt: string | null
  openedAt?: string | null
  slaStartedAt?: string | null
  targetMinutes?: number | null
  isOla?: boolean
}

function MiniTimerBadge({ timer, now }: { timer: MiniTimer; now: number }): JSX.Element | null {
  if (!timer.dueAt) return null
  const due = timestampMs(timer.dueAt)

  // ── Met ──
  if (timer.completedAt) {
    const done = timestampMs(timer.completedAt)
    const onTime = done <= due
    return (
      <span
        title={`${timer.label}: ${onTime ? 'Met on time' : 'Met late'} — Due ${formatDateTime(timer.dueAt)}`}
        className={cn(
          'inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[9px] font-semibold',
          onTime ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600',
        )}
      >
        <CheckCircle2 className="h-2.5 w-2.5" />
        {timer.label}
      </span>
    )
  }

  const delta = due - now

  // ── Breached ──
  if (delta <= 0) {
    return (
      <span
        title={`${timer.label}: ${humanizeLive(delta)} overdue — Due ${formatDateTime(timer.dueAt)}`}
        className="inline-flex items-center gap-0.5 rounded-full bg-red-50 px-1.5 py-[1px] text-[9px] font-bold text-red-600 animate-pulse"
      >
        <XCircle className="h-2.5 w-2.5" />
        {timer.label} {humanizeLive(delta)}
      </span>
    )
  }

  // ── Running / Approaching ──
  // Compute progress to pick a color: emerald (fresh) → blue → amber → orange (almost breached)
  const startTs = timer.slaStartedAt ?? timer.openedAt
  const start = startTs ? timestampMs(startTs) : due - (timer.targetMinutes ? timer.targetMinutes * 60_000 : 0)
  const budget = Math.max(1, timer.targetMinutes ? timer.targetMinutes * 60_000 : due - start)
  const elapsed = Math.max(0, now - start)
  const progress = Math.min(1, elapsed / budget)

  let bgClass: string
  if (progress < 0.5) {
    bgClass = timer.isOla ? 'bg-violet-50 text-violet-600' : 'bg-emerald-50 text-emerald-600'
  } else if (progress < 0.75) {
    bgClass = timer.isOla ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'
  } else if (progress < 0.9) {
    bgClass = 'bg-amber-50 text-amber-600'
  } else {
    bgClass = 'bg-orange-50 text-orange-600'
  }

  const isNear = progress >= 0.8 || delta < 30 * 60 * 1000
  const dotColor = isNear ? 'bg-amber-500' : timer.isOla ? 'bg-violet-500' : progress < 0.5 ? 'bg-emerald-500' : 'bg-blue-500'

  return (
    <span
      title={`${timer.label}: ${humanizeLive(delta)} remaining — Due ${formatDateTime(timer.dueAt)}`}
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full px-1.5 py-[1px] text-[9px] font-semibold tabular-nums',
        bgClass,
      )}
    >
      {isNear ? (
        <AlertTriangle className="h-2.5 w-2.5 animate-pulse" />
      ) : (
        <span className={cn('h-1.5 w-1.5 rounded-full', dotColor, 'animate-pulse')} />
      )}
      {timer.label} {humanizeLive(delta)}
    </span>
  )
}

export default function ConversationListItem(props: ConversationListItemProps): JSX.Element {
  const { entry, selected, onClick, isFirst, isLast, now, ratingScale } = props
  const navigate = useNavigate()
  const config = getChannelConfig(entry.channelOrigin)
  const Icon = config.icon

  // Format customer name cleanly
  const resolvedName = formatCustomerName(
    entry.displayName,
    entry.primaryHandle,
    entry.channelOrigin,
  )
  const initials = getCustomerInitials(resolvedName)

  const handleDoubleClick = () => {
    navigate(`/agent/conversation/view/${entry.id}`)
  }

  const slaTimers: MiniTimer[] = [
    { label: 'R', dueAt: entry.firstResponseDueAt, completedAt: entry.firstRespondedAt, openedAt: entry.openedAt, slaStartedAt: entry.slaStartedAt, targetMinutes: null },
    { label: 'Res', dueAt: entry.resolutionDueAt, completedAt: entry.resolvedAt, openedAt: entry.openedAt, slaStartedAt: entry.slaStartedAt, targetMinutes: null },
  ].filter((t) => t.dueAt !== null)
  const olaTimers: MiniTimer[] = entry.olaDueAt
    ? [{ label: 'OLA', dueAt: entry.olaDueAt, completedAt: entry.firstRespondedAt, openedAt: entry.openedAt, slaStartedAt: entry.slaStartedAt, targetMinutes: null, isOla: true }]
    : []
  const allTimers = [...slaTimers, ...olaTimers]

  return (
    <button
      type="button"
      onClick={onClick}
      onDoubleClick={handleDoubleClick}
      className={cn(
        'group flex w-full gap-2.5 px-3 py-2.5 text-left transition-all duration-150',
        isFirst && 'rounded-t-xl',
        isLast && 'rounded-b-xl',
        selected
          ? 'bg-[#F8FAFC] shadow-[inset_3px_0_0_0_#16273F]'
          : 'bg-white hover:bg-[#F8FAFC]',
      )}
    >
      {/* Avatar with initials + channel icon badge */}
      <div className="relative mt-0.5 shrink-0">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-gray-200 to-gray-300 text-[11px] font-bold text-gray-700 transition-transform group-hover:scale-105',
          )}
        >
          {initials}
        </div>
        <div
          className={cn(
            'absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-white',
            config.bg,
          )}
        >
          <Icon
            className={cn('h-2.5 w-2.5', config.color)}
            data-testid={`channel-icon-${entry.channelOrigin}`}
          />
        </div>
        {(entry.status === 'open' || entry.status === 'reopen') && (
          <div className="absolute -left-0.5 -top-0.5 h-2 w-2 rounded-full border-[1.5px] border-white bg-emerald-500" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        {/* Top row: Name + Time */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={cn(
              'truncate text-[13px] leading-5',
              selected ? 'font-semibold text-gray-900' : 'font-medium text-gray-800',
            )}
          >
            {resolvedName}
          </span>
          <span className="shrink-0 text-[11px] tabular-nums text-gray-400">
            {relativeTime(entry.lastMessageAt)}
          </span>
        </div>

        {/* Bottom row: Conversation ref + meta + badges */}
        <div className="mt-0.5 flex items-center gap-2">
          <span className="shrink-0 text-[10px] font-mono text-gray-400">#{entry.referenceId}</span>

          <span className="h-3 w-px bg-gray-200" />

          <div className="flex shrink-0 items-center gap-1">
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                SLA_COLORS[entry.slaState],
                (entry.slaState === 'breached' || entry.slaState === 'approaching') && 'animate-pulse',
              )}
              data-testid="sla-dot"
              aria-label={`SLA ${entry.slaState}`}
              title={`SLA ${entry.slaState}${entry.slaPolicyName ? ` — ${entry.slaPolicyName}` : ''}`}
            />
            <span className="text-[10px] text-gray-400">{config.label}</span>
          </div>

          {entry.parentReferenceId && (
            <span
              className="inline-flex min-w-0 shrink items-center gap-1 rounded-full bg-violet-50 px-1.5 py-[1px] text-[9px] font-semibold text-violet-700"
              title={`Child of #${entry.parentReferenceId}`}
            >
              <GitBranch className="h-2.5 w-2.5 shrink-0" />
              <span className="truncate">#{entry.parentReferenceId}</span>
            </span>
          )}

          {/* SLA / OLA mini timer badges */}
          {allTimers.length > 0 && (
            <div className="flex shrink-0 items-center gap-1">
              <span className="h-3 w-px bg-gray-200" />
              {slaTimers.map((t, i) => (
                <MiniTimerBadge key={`sla-${i}`} timer={t} now={now} />
              ))}
              {olaTimers.map((t, i) => (
                <MiniTimerBadge key={`ola-${i}`} timer={t} now={now} />
              ))}
            </div>
          )}

          <div className="flex min-w-0 items-center gap-1 overflow-hidden">
            {entry.transferCount > 0 && (
              <span className="shrink-0 rounded-full bg-amber-50 px-1.5 py-[1px] text-[9px] font-medium text-amber-600">
                T{entry.transferCount}
              </span>
            )}
            {entry.priorityId && (
              <span title="Priority" className="shrink-0 rounded-full bg-red-50 px-1.5 py-[1px] text-[9px] font-semibold text-red-600">
                P
              </span>
            )}
            {entry.urgencyId && (
              <span title="Urgency" className="shrink-0 rounded-full bg-orange-50 px-1.5 py-[1px] text-[9px] font-semibold text-orange-600">
                U
              </span>
            )}
            {entry.approvalStatus && entry.approvalStatus !== 'none' && (
              <span title="Approval required" className="shrink-0 rounded-full bg-yellow-50 px-1.5 py-[1px] text-[9px] font-semibold text-yellow-600">
                A
              </span>
            )}
            {entry.csatScore && (
              <span title={`CSAT ${entry.csatScore}/${ratingScale}`} className="shrink-0 rounded-full bg-teal-50 px-1.5 py-[1px] text-[9px] font-semibold text-teal-600">
                {entry.csatScore}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}
